import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { Warning, SignOut, ArrowCounterClockwise, ChatCircleDots, DownloadSimple } from "@phosphor-icons/react";
import LifecycleChecklist from "./LifecycleChecklist";

export default function OffboardingPanel() {
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [log, setLog] = useState([]);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);
  const [ckKey, setCkKey] = useState(0);

  const loadReq = useCallback(() => {
    api.get("/offboarding/request").then((r) => { setReq(r.data.request); setReasons(r.data.exit_reasons || []); }).catch(() => {});
  }, []);
  const loadLog = useCallback(() => {
    api.get("/offboarding/users").then((r) => setLog(r.data.entries || [])).catch(() => {});
  }, []);
  useEffect(() => { loadReq(); loadLog(); }, [loadReq, loadLog]);

  const isCost = reason === "Cost / budget";

  const submit = async (contact) => {
    if (!reason) { toast.error("Please choose a reason."); return; }
    setBusy(true);
    try {
      await api.post("/offboarding/request", { reason, detail: detail.trim(), contact_requested: !!contact });
      toast.success("Offboarding request submitted to ClaroCompliance.");
      setOpen(false); setReason(""); setDetail(""); loadReq();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your offboarding request?")) return;
    try { await api.delete("/offboarding/request"); toast.success("Offboarding request cancelled."); loadReq(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reactivate = async (id) => {
    try { await api.post(`/offboarding/users/${id}/reactivate`); toast.success("User reactivated."); loadLog(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadBundle = async () => {
    setDl(true);
    try {
      await downloadBlob("/offboarding/data-bundle", "claroshield-export.zip");
      await api.post("/lifecycle/offboarding/data_export", { done: true });
      setCkKey((k) => k + 1);
      toast.success("Your data bundle is downloading.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed."); }
    finally { setDl(false); }
  };

  return (
    <div className="space-y-8" data-testid="offboarding-panel">
      {/* Request status / start */}
      <div className="border border-border bg-card p-6">
        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
          <SignOut size={20} weight="duotone" /> Offboarding
        </h2>
        {!req ? (
          <>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Starting offboarding notifies ClaroCompliance and opens the checklist below. You can complete every
              customer step yourself — the final removal of your entity is performed by ClaroCompliance.
            </p>
            <Button data-testid="offboarding-start-btn" onClick={() => setOpen(true)}
              className="mt-4 rounded-none bg-red-600 hover:bg-red-700">
              <SignOut size={15} className="mr-1.5" /> Begin offboarding
            </Button>
          </>
        ) : (
          <div className="mt-3 border border-amber-300 bg-amber-50 p-4" data-testid="offboarding-status">
            <div className="flex items-start gap-2">
              <Warning size={18} weight="fill" className="text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-900">
                <div className="font-semibold">Offboarding requested</div>
                <div>Reason: <b>{req.reason}</b>{req.detail ? ` — ${req.detail}` : ""}</div>
                <div className="text-[12px] mt-0.5">Requested {new Date(req.requested_at).toLocaleString()} · status: {req.status}</div>
                {req.contact_requested && <div className="text-[12px] mt-0.5">You asked ClaroCompliance to reach out about options.</div>}
                <div className="text-[12px] mt-1">ClaroCompliance will perform the final entity removal. Complete the checklist below in the meantime.</div>
              </div>
            </div>
            <Button variant="outline" size="sm" data-testid="offboarding-cancel-btn" onClick={cancel} className="mt-3 rounded-none">
              Cancel request
            </Button>
          </div>
        )}
      </div>

      {/* Offboarding checklist */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="text-lg font-bold text-foreground">Offboarding checklist</h3>
            <p className="text-sm text-muted-foreground">Track each step. Steps marked ClaroCompliance are completed by us.</p>
          </div>
          <Button data-testid="offboarding-download-bundle" onClick={downloadBundle} disabled={dl}
            variant="outline" className="rounded-none shrink-0">
            <DownloadSimple size={15} className="mr-1.5" /> {dl ? "Preparing…" : "Download data bundle (.zip)"}
          </Button>
        </div>
        <LifecycleChecklist key={ckKey} type="offboarding" />
      </div>

      {/* Offboarding log — deactivated users */}
      <div className="border border-border bg-card p-6">
        <h3 className="text-lg font-bold text-foreground">Offboarding log</h3>
        <p className="text-sm text-muted-foreground mt-1">Users who left this entity — reactivate with one click.</p>
        {log.length === 0 ? (
          <div className="text-sm text-muted-foreground mt-4" data-testid="offboarding-log-empty">No one has left this entity yet.</div>
        ) : (
          <table className="w-full text-sm mt-4" data-testid="offboarding-log-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="py-2">User</th><th className="py-2">Left</th><th className="py-2">By</th><th className="py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {log.map((e) => (
                <tr key={e.id} className="border-b border-border" data-testid={`offboarding-log-row-${e.id}`}>
                  <td className="py-2">
                    <div className="font-medium text-foreground">{e.name || e.email}</div>
                    <div className="text-[12px] text-muted-foreground">{e.email}{e.role ? ` · ${e.role}` : ""}</div>
                  </td>
                  <td className="py-2 text-[12px] text-muted-foreground font-mono">{new Date(e.removed_at).toLocaleDateString()}</td>
                  <td className="py-2 text-[12px] text-muted-foreground">{e.removed_by}</td>
                  <td className="py-2 text-right">
                    {e.reactivated ? (
                      <span className="text-[12px] text-emerald-700 font-semibold">Reactivated</span>
                    ) : e.user_deleted ? (
                      <span className="text-[12px] text-muted-foreground">Deleted</span>
                    ) : (
                      <Button size="sm" variant="outline" data-testid={`offboarding-reactivate-${e.id}`}
                        onClick={() => reactivate(e.id)} className="rounded-none">
                        <ArrowCounterClockwise size={14} className="mr-1" /> Reactivate
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Exit survey modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none" data-testid="offboarding-survey-modal">
          <DialogHeader><DialogTitle>Before you go</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Why are you leaving?</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="offboarding-reason-select" className="rounded-none mt-1"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => <SelectItem key={r} value={r} data-testid={`offboarding-reason-${r}`}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Anything else? (optional)</label>
              <textarea data-testid="offboarding-detail-input" value={detail} rows={3} maxLength={1000}
                onChange={(e) => setDetail(e.target.value)}
                className="mt-1 w-full border border-border bg-background p-2 text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            {isCost && (
              <div className="border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900" data-testid="offboarding-retention">
                <div className="flex items-start gap-2">
                  <ChatCircleDots size={18} weight="fill" className="text-sky-600 mt-0.5" />
                  <div>
                    <b>If cost is the concern, let's talk.</b> We may have a plan or arrangement that works for you.
                    You can reach out to a ClaroCompliance rep — or continue ending your service without one.
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {isCost && (
              <Button variant="outline" data-testid="offboarding-contact-btn" disabled={busy}
                onClick={() => submit(true)} className="rounded-none">
                <ChatCircleDots size={15} className="mr-1.5" /> Contact ClaroCompliance
              </Button>
            )}
            <Button data-testid="offboarding-continue-btn" disabled={busy} onClick={() => submit(false)}
              className="rounded-none bg-red-600 hover:bg-red-700">
              {busy ? "Submitting…" : "Continue offboarding"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
